/**
 * The category sidebar.
 *
 * Draws itself from `navigation.ts`, filtered by what the caller may reach. A
 * section whose every item is hidden disappears with its heading — a lone
 * heading over nothing looks like a rendering fault.
 */
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { Logo } from '../components/Logo';
import { useAuth } from '../auth/useAuth';
import { GLASS_RADIUS, SHELL_GAP, TOP_BAR_HEIGHT, glass } from '../theme/theme';
import { MODULE_NAMESPACES } from '../modules/locales';
import { useModules } from '../modules/useModules';
import { isCurrent, navigationWith } from './navigation';

export const SIDEBAR_WIDTH = 256;

/**
 * One block of the sidebar: a section, or the superadmin note.
 *
 * Attached, it is rounded on the left only and flush with the drawer's right
 * edge, which is where the main panel begins — so each group reads as a tab
 * attached to it. Detached, in the drawer that slides over the page, there is
 * no panel to attach to: the same gap and the same corners on every side.
 */
function groupSx(attached: boolean) {
  return (theme: Theme) => ({
    ...glass(theme, 'group'),
    ml: SHELL_GAP,
    mr: attached ? 0 : SHELL_GAP,
    mb: SHELL_GAP,
    borderRadius: attached
      ? `${GLASS_RADIUS}px 0 0 ${GLASS_RADIUS}px`
      : `${GLASS_RADIUS}px`,
  });
}

export interface SidebarProps {
  /**
   * Whether the groups attach to the main panel beside the sidebar: true in
   * the permanent drawer, false in the one that slides over the page.
   */
  attached: boolean;
  /** Closes the temporary drawer after navigating. No-op on a wide viewport. */
  onNavigate?: () => void;
}

export function Sidebar({ attached, onNavigate }: SidebarProps) {
  // Module namespaces too: a module page's menu label is `crm:nav.…`, and a
  // section a module declares is titled in its namespace.
  const { t } = useTranslation(['nav', 'common', ...MODULE_NAMESPACES]);
  const { canAny, isSuperadmin } = useAuth();
  const { pathname } = useLocation();
  const modules = useModules();

  const sections = navigationWith(modules).map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.anyOf || canAny(...item.anyOf)),
  })).filter((section) => section.items.length > 0);

  const group = groupSx(attached);
  // Attached, the groups keep clear of the main panel's rounded corners: they
  // start and end a corner radius inside its top and bottom, where its edge is
  // straight. Detached, there are no corners to keep clear of.
  const cornerClearance = attached ? `${GLASS_RADIUS}px` : 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: cornerClearance }}>
      {/* Exactly as tall as the top bar, so the logo block and the header meet
          in one unbroken line across the drawer edge. */}
      <Box
        sx={{
          px: 2.5,
          height: TOP_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Logo size={34} withWordmark subtitle={t('common:product.tagline')} />
      </Box>

      {/* A margin, not padding: a scrolled group is clipped here rather than
          sliding into the corner. */}
      <Box sx={{ flex: 1, overflowY: 'auto', mt: cornerClearance }}>
        {sections.map((section) => (
          <List
            key={section.id}
            dense
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  lineHeight: 2.5,
                }}
              >
                {t(section.titleKey)}
              </ListSubheader>
            }
            sx={[group, { px: 1.5, py: 0.5 }]}
          >
            {section.items.map((item) => {
              const selected = isCurrent(item, pathname);
              const Icon = item.icon;

              return (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.exact ?? false}
                  selected={selected}
                  onClick={onNavigate}
                  sx={{
                    borderRadius: 2,
                    mb: 0.25,
                    // The selected row carries the brand colour; everything else
                    // stays quiet, so one glance finds where you are.
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      color: 'primary.main',
                      '& .MuiListItemIcon-root': { color: 'primary.main' },
                      '&:hover': { bgcolor: 'action.selected' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    slotProps={{
                      // Typography props only — the size and weight are styles,
                      // so they travel through sx rather than as bare props.
                      primary: { sx: { fontSize: 14, fontWeight: selected ? 600 : 500 } },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>

      {/* A group of its own: on the bare background its small print would
          sit on whatever the photograph has there. */}
      {isSuperadmin && (
        <Stack sx={[group, { px: 2, py: 1.5, flexShrink: 0 }]} spacing={0.25}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'secondary.main' }}>
            {t('common:superadmin.chip')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('common:superadmin.bypass')}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
